import {
  fileDestination,
  retrieveFile,
  retrieveFiles,
  uploadFile,
} from "./canonical-files";
import type { CanonicalFile, FileDestination } from "@/lib/data/schemas/files";
import {
  ensureFileNameHasExtension,
  getFileNameParts,
} from "@/lib/utils/matterFile";

const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export const editorSaveUnavailableReason = (file: CanonicalFile) => {
  if (file.scope === "template")
    return "Use the Templates screen to edit templates.";
  if (!file.capabilities.canUploadVersion)
    return "Saving a new version is unavailable for this file.";
  if (
    file.contentType === DOCX_MIME &&
    getFileNameParts(file.name).fileExtension.toLowerCase() !== "docx"
  )
    return "Rename this file with a .docx extension before saving a version, or save a copy.";
  return undefined;
};

export const editorFileDestination = (
  file: CanonicalFile,
  parentId = file.parentId,
) =>
  fileDestination(
    file.scope,
    parentId,
    file.massTortId || file.matterId || undefined,
  );

export async function saveEditedFile(
  file: CanonicalFile,
  blob: Blob,
  contentType: string,
) {
  const current = await retrieveFile(file.id);
  if (current.revision !== file.revision)
    throw new Error(
      "This file changed while you were editing it. Save a copy or reopen the latest version.",
    );
  if (current.scope === "template" || !current.capabilities.canUploadVersion)
    throw new Error(
      "Saving a new version is not available for this file. You can save a copy.",
    );
  return uploadFile({
    file: new File([blob], file.name, { type: blob.type || contentType }),
    name: file.name,
    destination: editorFileDestination(file),
    targetFileId: file.id,
    expectedMode: "version",
    expectedRevision: file.revision,
  });
}

async function uniqueCopyName(
  sourceName: string,
  destination: FileDestination,
) {
  const names = new Set<string>();
  const cursors = new Set<string>();
  let cursor: string | undefined;
  do {
    const page = await retrieveFiles(destination, cursor);
    for (const file of page.data) names.add(file.name.toLowerCase());
    cursor = page.nextCursor ?? undefined;
    if (cursor && cursors.has(cursor))
      throw new Error("Unable to finish loading this folder. Please refresh.");
    if (cursor) cursors.add(cursor);
  } while (cursor);
  const { fileName, fileExtension } = getFileNameParts(sourceName);
  const base = fileName || "Edited document";
  const extension = fileExtension ? `.${fileExtension}` : "";
  let index = 1;
  let candidate: string;
  do {
    candidate = `${base} copy${index === 1 ? "" : ` ${index}`}${extension}`;
    index += 1;
  } while (names.has(candidate.toLowerCase()));
  return candidate;
}

export async function saveEditedCopy(
  file: CanonicalFile,
  {
    blob,
    contentType,
    sourceName,
    name,
    folderId,
  }: {
    blob: Blob;
    contentType: string;
    sourceName?: string;
    name?: string;
    folderId?: string;
  },
) {
  const destination = editorFileDestination(
    file,
    folderId === undefined ? file.parentId : folderId || null,
  );
  const extension =
    contentType === DOCX_MIME
      ? "docx"
      : contentType === "application/pdf"
        ? "pdf"
        : "";
  const originalName = sourceName || file.name;
  const sourceBase = getFileNameParts(originalName).fileName || originalName;
  const copyName = name
    ? ensureFileNameHasExtension(name, extension)
    : await uniqueCopyName(
        extension ? `${sourceBase}.${extension}` : originalName,
        destination,
      );
  return uploadFile({
    file: new File([blob], copyName, { type: blob.type || contentType }),
    name: copyName,
    destination,
    expectedMode: "create",
  });
}
